import React from 'react';
import ReportShell from '../../../shared/ReportShell';
import PricelistAboutUs from './PricelistAboutUs';
import PricelistContactBlock from './PricelistContactBlock';
import { AvailabilityCornerBadge, isStackedAvailabilityBadge } from '../../../shared/Pill';
import { formatDate, formatDateWords } from '../../../shared/tenantClock';
import { formatMoney, formatNumber } from '../../../shared/numberFormat';

// Print/PDF version of Pricelist 2.0's on-screen layout (Pricelist2Page.js)
// — same per-Category-section, per-Product-card grouping, wrapped in
// ReportShell for the standard Print/Save-as-PDF button (window.print(),
// no PDF library — see pat-architecture-reports-ui-standard.md). No CSV
// action; this is a printed document, not a data export.
function formatPrice(price) {
  return formatMoney(price) || '—';
}

// Date only, no time — unlike formatReportTimestamp() (ReportShell.js),
// which every other report uses. A Pricelist is browsed like a catalog,
// not audited down to the minute it was pulled, so the time only added
// noise here.
function formatReportDate(date = new Date()) {
  return `Generated ${formatDateWords(date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function groupByProduct(rows) {
  const groups = [];
  rows.forEach((row) => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.product_id === row.product_id) {
      lastGroup.rows.push(row);
    } else {
      groups.push({
        product_id: row.product_id,
        sku: row.sku,
        name: row.name,
        availability_state: row.availability_state,
        availability_badge: row.availability_badge,
        availability_badge_color: row.availability_badge_color,
        availability_date: row.availability_date,
        base_rrp: row.base_rrp,
        base_uom_code: row.base_uom_code,
        rows: [row],
      });
    }
  });
  return groups;
}

// Pairs up consecutive product groups for the 2-up table (below) — [a, b,
// c] becomes [[a, b], [c]], the trailing odd one left with an empty second
// cell rather than dropped.
function pairUp(items) {
  const pairs = [];
  for (let i = 0; i < items.length; i += 2) {
    pairs.push([items[i], items[i + 1]]);
  }
  return pairs;
}

function ProductCard({ group, defaultTaxRate, showRrp }) {
  return (
    <div style={{ background: '#fff', borderRadius: 6, overflow: 'hidden', border: '1px solid #eee' }}>
      <div style={{ position: 'relative', padding: '6px 10px', fontWeight: 600, fontSize: 13, background: '#f6f8fa', borderBottom: '1px solid #eee' }}>
        {/* position: absolute — see AvailabilityCornerBadge's own comment.
            Can never grow this header regardless of name length. The SKU
            line reserves the badge's ~90px so it can't run under it; the
            title below gets the full width, except under a stacked (Order
            Now) badge, whose second pill reaches down beside the title's
            first line. */}
        <div style={{ paddingRight: group.availability_badge ? 100 : 0 }}>
          {group.sku}
          {/* One RRP per Product, per Base UoM unit (ex GST Base UoM Retail
              Price as of Valid From, converted to inc GST) — sits inline
              after the SKU so it adds no height. Blank when none is in force. */}
          {showRrp && defaultTaxRate !== null && group.base_rrp !== null && group.base_rrp !== undefined && (
            <span style={{ color: '#3a6ea5', fontWeight: 600 }}>
              {' · '}RRP inc GST {formatPrice(group.base_rrp * (1 + defaultTaxRate))}/{group.base_uom_code}
            </span>
          )}
        </div>
        <div style={{ paddingRight: isStackedAvailabilityBadge(group.availability_badge) ? 100 : 0 }}>{group.name}</div>
        <AvailabilityCornerBadge badge={group.availability_badge} date={group.availability_date} color={group.availability_badge_color} />
      </div>
      {group.rows.map((row, idx) => {
        const isNewUom = idx === 0 || group.rows[idx - 1].uom_id !== row.uom_id;
        const hasBaseQty = row.base_quantity !== row.min_quantity;
        return (
          <div
            key={`${row.uom_id}-${row.min_quantity}-${idx}`}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '3px 10px', fontSize: 12, borderTop: isNewUom ? '1px solid #eee' : 'none' }}
          >
            <span style={{ color: '#555' }}>
              {isNewUom ? row.uom_name : ''} {formatNumber(row.min_quantity, { maxDecimals: 4 })}+
              {hasBaseQty && (
                <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>{formatNumber(row.base_quantity, { maxDecimals: 4 })} {row.base_uom_name}</div>
              )}
            </span>
            <span style={{ textAlign: 'right' }}>
              {row.ambiguous ? (
                <span style={{ color: '#b25f00' }}>Ambiguous</span>
              ) : row.needs_price ? (
                <span style={{ color: '#888' }}>Needs price</span>
              ) : (
                <>
                  {formatPrice(row.price)}
                  <span style={{ color: '#888', fontWeight: 400 }}> ({formatPrice(row.equiv_price)}/{row.base_uom_name})</span>
                  {defaultTaxRate !== null && (
                    <div style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>
                      Inc GST: {formatPrice(row.price * (1 + defaultTaxRate))}
                    </div>
                  )}
                </>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function groupByCategory(rows) {
  const sections = [];
  rows.forEach((row) => {
    const lastSection = sections[sections.length - 1];
    if (lastSection && lastSection.category_id === row.product_category_id) {
      lastSection.rows.push(row);
    } else {
      sections.push({ category_id: row.product_category_id, category_name: row.product_category_name, rows: [row] });
    }
  });
  return sections;
}

// `companyInfo` — see useCompanyName.js — threaded through to
// PricelistAboutUs/PricelistContactBlock for the server-rendered PDF
// attachment path (issue #238); undefined for the normal on-screen/print
// use, which fetches its own via those components' own hook call.
function PricelistCompactReport({ pricelist, result, defaultTaxRate, showRrp, onClose, companyInfo }) {
  const rows = result?.rows || [];
  const isEmpty = rows.length === 0;

  return (
    <ReportShell
      title={`${pricelist.name} Pricelist`}
      subtitle={(
        <>
          {/* Screen-only here — printed via footerNote below, in the same
              running-footer box as "Commercial in Confidence" (same split
              Extended Pricelist uses), so it doesn't compete with the
              title/category for room at the top of a printed page. */}
          <div className="no-print">{formatReportDate()}</div>
          <div>
            {pricelist.customer_price_group?.name} · {pricelist.product_price_group?.name || 'All products'}
            {pricelist.uom ? ` · ${pricelist.uom.code}` : ''} · Valid {formatDate(result?.validFrom)}
            {result?.validTo !== result?.validFrom ? ` to ${formatDate(result?.validTo)}` : ''}
          </div>
        </>
      )}
      footerNote={`Compact Pricelist · ${formatReportDate()}`}
      onClose={onClose}
      printDisabled={isEmpty}
      companyInfo={companyInfo}
    >
      {isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No sellable Product/UoM combinations match this scope.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <PricelistAboutUs variant="compact" companyInfo={companyInfo} />
          {groupByCategory(rows).map((section, sIdx) => (
            <div
              key={section.category_id ?? 'uncategorised'}
              // padding-top only (not the .print-area padding-top trick,
              // which only applies once at the top of a box spanning many
              // pages) — this div gets its own fresh page via the break
              // below, so its own padding DOES apply at that page's top,
              // unlike a table row/cell that merely overflows onto a
              // continuation page. Without it the category heading (outside
              // any table, so the <thead> spacer trick doesn't reach it)
              // collided with the running header on every category after
              // the first. Verified empirically before landing here — see
              // the print-only-row/<thead> spacer comment below for the
              // sibling fix this pairs with.
              style={sIdx > 0 ? { breakBefore: 'page', pageBreakBefore: 'always', paddingTop: 26 } : undefined}
            >
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 10px', paddingBottom: 6, borderBottom: '2px solid #dde4ec' }}>
                {section.category_name}
              </h3>
              {/* 2-up table, not a CSS Grid — a card only needs ~4 narrow
                  fields, so stacking full-width wasted most of the page's
                  horizontal space, and the old single column only fit ~5
                  products/page. CSS Grid gave the same 2-up layout and
                  paginated correctly in isolated testing (headless Chromium
                  + pdfjs: no split cards, natural row-by-row reading order,
                  ~2.8x more cards/page) — but a plain <div> grid has no
                  equivalent to a table's repeating <thead>, so there was
                  nothing to reserve clearance from the running header on a
                  continuation page (the same collision Extended Pricelist's
                  own <thead> spacer row already solves — see that file and
                  the App.css comment on .print-running-header). Rebuilt as
                  an actual <table> — one <tr> per pair of products — purely
                  to get that same reliable per-page spacer back, verified
                  the same way before landing here.
                  Per-card <thead>/column-header row is still gone — "UoM
                  Qty+ · Price (equiv/unit)" is self-describing on one line,
                  so repeating those labels on every card was pure overhead.
                  This view is for a customer who already knows the format
                  (Extended Pricelist is the one with the explanatory
                  layout), so no header row is a deliberate loss, not an
                  oversight. */}
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '50%' }} />
                  <col style={{ width: '50%' }} />
                </colgroup>
                <thead>
                  <tr className="print-only-row"><td colSpan={2} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
                </thead>
                <tbody>
                  {pairUp(groupByProduct(section.rows)).map(([left, right]) => (
                    <tr key={left.product_id} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td style={{ verticalAlign: 'top', padding: 5 }}><ProductCard group={left} defaultTaxRate={defaultTaxRate} showRrp={showRrp} /></td>
                      <td style={{ verticalAlign: 'top', padding: 5 }}>{right && <ProductCard group={right} defaultTaxRate={defaultTaxRate} showRrp={showRrp} />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <PricelistContactBlock companyInfo={companyInfo} />
        </div>
      )}
    </ReportShell>
  );
}

export default PricelistCompactReport;
